<template>
  <div class="lp">
    <!-- Selected state -->
    <div v-if="selected" class="lp-selected">
      <div class="lp-selected-icon" :style="{ background: tone }">
        <i :class="iconClass"/>
      </div>
      <div class="lp-selected-main">
        <div class="lp-selected-eyebrow">{{ libraryLabel }} · Selected</div>
        <div class="lp-title-row">
          <span class="lp-selected-title">{{ selected.title }}</span>
          <span v-if="selected.isProgramOnly" class="lp-scope-pill">Program only</span>
        </div>
        <div v-if="selected.meta" class="lp-selected-meta">{{ selected.meta }}</div>
      </div>
      <div class="lp-selected-actions">
        <el-popover
          v-if="isTruncatable(selected)"
          placement="left-start"
          :width="360"
          trigger="click"
          popper-class="lp-preview-popper"
        >
          <div class="lp-preview-title">{{ selected.title }}</div>
          <div class="lp-preview-body">{{ selected.meta }}</div>
          <button
            slot="reference"
            type="button"
            class="lp-result-preview"
            aria-label="Preview"
          ><i class="el-icon-view"/></button>
        </el-popover>
        <button type="button" class="lp-replace" @click="clear">Replace</button>
      </div>
    </div>

    <!-- Empty state with intent line + picker -->
    <div v-else class="lp-empty">
      <div class="lp-intent" v-if="intent">
        <i class="el-icon-magic-stick"/>
        <span>{{ intent }}</span>
      </div>

      <div class="lp-search">
        <i class="el-icon-search"/>
        <el-input
          :value="query"
          @input="onQuery"
          :placeholder="searchPlaceholder"
        />
      </div>

      <div v-if="filtered.length" class="lp-results">
        <div
          v-for="item in filtered"
          :key="item.id"
          class="lp-result"
          role="button"
          tabindex="0"
          @click="pick(item)"
          @keydown.enter.prevent="pick(item)"
          @keydown.space.prevent="pick(item)"
        >
          <div class="lp-result-icon" :style="{ background: tone }">
            <i :class="iconClass"/>
          </div>
          <div class="lp-result-main">
            <div class="lp-title-row">
              <span class="lp-result-title">{{ item.title }}</span>
              <span v-if="item.isProgramOnly" class="lp-scope-pill">Program only</span>
            </div>
            <div v-if="item.meta" class="lp-result-meta">{{ item.meta }}</div>
          </div>
          <div class="lp-result-actions">
            <el-popover
              v-if="isTruncatable(item)"
              placement="left-start"
              :width="360"
              trigger="click"
              popper-class="lp-preview-popper"
            >
              <div class="lp-preview-title">{{ item.title }}</div>
              <div class="lp-preview-body">{{ item.meta }}</div>
              <button
                slot="reference"
                type="button"
                class="lp-result-preview"
                aria-label="Preview"
                @click.stop
              ><i class="el-icon-view"/></button>
            </el-popover>
            <span class="lp-result-pick">Pick</span>
          </div>
        </div>
      </div>

      <div v-else-if="loading" class="lp-empty-hint lp-loading" role="status">
        <i class="el-icon-loading"/> Loading {{ libraryLabel }}…
      </div>
      <div v-else-if="loadError" class="lp-empty-hint lp-load-error" role="alert">
        <span>{{ loadError }}</span>
        <button type="button" class="lp-retry" @click="$emit('retry')">Try again</button>
      </div>
      <div v-else-if="query" class="lp-noresults">
        Nothing matches "{{ query }}".
        <a v-if="createPath" class="lp-create" :href="createPath" target="_blank">
          Create one in {{ libraryLabel }} <i class="el-icon-top-right"/>
        </a>
      </div>

      <!-- Empty-query fallback. `filtered` shows the top rows immediately,
           so this only appears if the component is passed an empty list
           after async loading settles. -->
      <div v-else-if="items.length" class="lp-empty-hint">
        Type to search across {{ items.length }} {{ items.length === 1 ? libraryLabel.toLowerCase() : libraryLabel.toLowerCase() + ' items' }}.
      </div>
      <div v-else class="lp-empty-hint">
        Nothing in {{ libraryLabel }} yet.
        <a v-if="createPath" class="lp-create" :href="createPath" target="_blank">
          Create one <i class="el-icon-top-right"/>
        </a>
      </div>

      <a v-if="!query && items.length && createPath" class="lp-create-foot" :href="createPath" target="_blank">
        Don't see what you need? Create one in {{ libraryLabel }} <i class="el-icon-top-right"/>
      </a>
    </div>
  </div>
</template>

<script>
// Cap on rows shown at once. The editor sorts Program-only items first,
// so showing the first rows immediately gives authors useful defaults
// while keeping large libraries compact.
const BROWSE_LIMIT = 5

export default {
  name: 'library-picker',
  props: {
    value: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    libraryLabel: { type: String, required: true },
    iconClass: { type: String, default: 'el-icon-document' },
    tone: { type: String, default: '#6c30cc' },
    searchPlaceholder: { type: String, default: 'Search…' },
    intent: { type: String, default: '' },
    createPath: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    loadError: { type: String, default: '' },
  },
  data() {
    return { query: '' }
  },
  computed: {
    selected() {
      if (!this.value) return null
      return this.items.find((it) => it.id === this.value) || null
    },
    filtered() {
      const q = this.query.trim().toLowerCase()
      // Adaptive: a small pool (<= BROWSE_LIMIT) is shown straight away
      // so the author can just pick — hiding 5 items behind a search box
      // makes program content needlessly hard to find. A larger pool shows
      // the first prioritized rows, then search can find anything else.
      if (!q) {
        return this.items.slice(0, BROWSE_LIMIT)
      }
      return this.items
        .filter((it) =>
          (it.title || '').toLowerCase().includes(q) ||
          (it.meta || '').toLowerCase().includes(q) ||
          // `search` is an optional pre-lowercased blob (e.g. a
          // question's text in every language) so matching isn't
          // limited to the displayed title's language.
          (it.search || '').includes(q)
        )
        .slice(0, BROWSE_LIMIT)
    },
  },
  methods: {
    onQuery(v) {
      this.query = v
    },
    pick(item) {
      this.$emit('input', item.id)
      this.query = ''
    },
    clear() {
      this.$emit('input', '')
    },
    // Gate the Preview affordance on metas the 2-line CSS clamp would
    // actually hide content from.
    isTruncatable(item) {
      const m = item && item.meta
      if (!m) return false
      return m.length > 90 || (m.split('\n').length - 1) >= 2
    },
  },
}
</script>

<style scoped>
.lp { display: flex; flex-direction: column; gap: 10px; }

/* Selected state */
.lp-selected {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid rgba(108, 48, 204, 0.20);
  border-radius: 10px;
  box-shadow: 0 0 0 4px rgba(108, 48, 204, 0.06);
}
.lp-selected-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  margin-top: 2px;
}
.lp-selected-main { min-width: 0; }
.lp-selected-eyebrow {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #00b383;
  margin-bottom: 2px;
}
.lp-selected-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
}
.lp-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
}
.lp-scope-pill {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f2ecfb;
  color: #6c30cc;
  font-size: 10px;
  font-weight: 800;
  line-height: 18px;
}
.lp-selected-meta {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}
.lp-selected-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.lp-replace {
  appearance: none;
  background: transparent;
  border: none;
  font-size: 12px;
  font-weight: 700;
  color: #6c30cc;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background .15s ease;
}
.lp-replace:hover { background: #f2ecfb; }

/* Empty state */
.lp-empty { display: flex; flex-direction: column; gap: 10px; }
.lp-loading { display: flex; align-items: center; gap: 7px; }
.lp-load-error { display: flex; align-items: center; justify-content: space-between; gap: 10px; color: #b62a2a; background: #fff7f7; border-color: #f1c4c4; }
.lp-retry { appearance: none; border: 0; background: transparent; color: #6c30cc; font-size: 12px; font-weight: 800; cursor: pointer; white-space: nowrap; }
.lp-intent {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px dashed #e2e4ea;
  border-radius: 8px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}
.lp-intent i { color: #c0c4cc; font-style: normal; }

.lp-search {
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 10px;
  align-items: center;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  padding: 6px 12px;
}
.lp-search i { color: #c0c4cc; }
.lp-search >>> .el-input__inner {
  border: none;
  padding: 0;
  height: 32px;
  background: transparent;
}

.lp-results {
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 10px;
  overflow: hidden;
}
.lp-result {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background .15s ease;
}
.lp-result:focus-visible {
  outline: 3px solid rgba(108, 48, 204, 0.24);
  outline-offset: -3px;
}
.lp-result:last-child { border-bottom: none; }
.lp-result:hover { background: rgba(108, 48, 204, 0.04); }
.lp-result-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  opacity: 0.85;
  margin-top: 1px;
}
.lp-result-main {
  min-width: 0; /* lets the clamp work inside the grid track */
}
.lp-result-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.35;
}
.lp-result-meta {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}
.lp-result-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}
.lp-result-preview {
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  color: #8a8a8a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background .15s ease, color .15s ease, border-color .15s ease;
}
.lp-result-preview:hover {
  background: #f2ecfb;
  color: #6c30cc;
  border-color: rgba(108, 48, 204, 0.20);
}
.lp-result-pick {
  font-size: 11px;
  font-weight: 700;
  color: #6c30cc;
}

.lp-noresults {
  font-size: 12px;
  color: #6b7280;
  padding: 10px 12px;
  background: #fafbfc;
  border-radius: 8px;
}
.lp-empty-hint {
  font-size: 12px;
  color: #8a8a8a;
  padding: 2px 4px;
}
.lp-empty-hint .lp-create {
  margin-left: 4px;
}
.lp-create, .lp-create-foot {
  font-size: 12px;
  font-weight: 700;
  color: #6c30cc;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
}
.lp-create:hover, .lp-create-foot:hover { text-decoration: underline; }
.lp-create-foot {
  align-self: flex-start;
  margin-left: 0;
  margin-top: 2px;
}
</style>

<style>
/* Preview popover for the library picker. Element UI teleports
   poppers out of component scope, so this block is unscoped. */
.lp-preview-popper {
  border-radius: 10px !important;
  border: 1px solid #e2e4ea !important;
  box-shadow: 0 12px 32px rgba(17, 24, 39, 0.10) !important;
  padding: 14px 16px !important;
  max-height: 360px;
  overflow-y: auto;
}
.lp-preview-popper .lp-preview-title {
  font-size: 13px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.005em;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef0f5;
}
.lp-preview-popper .lp-preview-body {
  font-size: 12px;
  color: #4a4a4a;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
